import { useEffect, useMemo, useRef, useState } from "react";
import { LandlordProfitSection } from "@/components/LandlordProfitSection";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { LOGO_URL } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Download, FileText, LogOut, Mail, ShieldCheck, Building2, BarChart3, CalendarClock, Upload, Trash2, Paperclip, AlertTriangle, Pencil, Users, Plus, Receipt, TrendingUp } from "lucide-react";
import { toast } from "sonner";
import { DarkModeToggle, useDarkMode } from "@/components/Layout";
import {
  ComposedChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";

const BASE = process.env.REACT_APP_BACKEND_URL;
const API = `${BASE}/api`;

const LANDLORD_TOKEN_KEY = "ns_landlord_token";
const money = (n) => `£${(n || 0).toFixed(2)}`;

function landlordClient() {
  const t = localStorage.getItem(LANDLORD_TOKEN_KEY);
  return axios.create({
    baseURL: API,
    headers: t ? { Authorization: `Bearer ${t}` } : {},
  });
}

function useQueryToken() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search).get("token"), [search]);
}

export default function LandlordPortal() {
  // Session Z78 — hook ensures `html.dark` is applied even on the
  // pre-signin phase so a late-night magic-link request matches the rest
  // of the CRM.
  useDarkMode();
  const urlToken = useQueryToken();
  const nav = useNavigate();
  const verifiedRef = useRef(null);
  const [phase, setPhase] = useState("boot"); // boot | request | signed_in
  const [busy, setBusy] = useState(false);
  const [email, setEmail] = useState("");
  const [sentTo, setSentTo] = useState("");
  const [landlord, setLandlord] = useState(null);
  const [month, setMonth] = useState(() => {
    const d = new Date();
    d.setMonth(d.getMonth() - 1);
    return d.toISOString().slice(0, 7);
  });
  const [data, setData] = useState(null);
  const [months, setMonths] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [docs, setDocs] = useState([]);
  const [docKind, setDocKind] = useState("lease");
  const [docExpiry, setDocExpiry] = useState("");
  const [uploading, setUploading] = useState(false);
  const [editingExpiry, setEditingExpiry] = useState(null); // {id, expires_on}
  const [brand, setBrand] = useState({ brand_name: "Newport Stays", brand_tagline: "Owner Portal", accent_color: "#0d7a75", powered_by_visible: true });
  const [bookingDetail, setBookingDetail] = useState(null);
  const [loadingBooking, setLoadingBooking] = useState(false);
  const [tenants, setTenants] = useState([]);
  const [tenantModal, setTenantModal] = useState(null); // {mode:'new'|'edit', tenant}
  const [expandedTenant, setExpandedTenant] = useState(null);
  const [tenantPayments, setTenantPayments] = useState([]);
  const [paymentForm, setPaymentForm] = useState({ paid_on: new Date().toISOString().slice(0, 10), amount: "", method: "", notes: "" });
  const [insights, setInsights] = useState(null);
  const [insightsMonths, setInsightsMonths] = useState(3);
  const [opCostTrend, setOpCostTrend] = useState(null);
  const [highlightPropId, setHighlightPropId] = useState(null);
  const [statementPrefs, setStatementPrefs] = useState({ auto_attach_cost_history: false });
  const [prefsBusy, setPrefsBusy] = useState(false);
  const [sendAllProgress, setSendAllProgress] = useState(null); // {sent, failed, total, index, tenant_name}
  const perUnitRowRefs = useRef({});

  // 1) If token present in URL — verify and store (guard against double-run under StrictMode)
  useEffect(() => {
    if (!urlToken) {
      const stored = localStorage.getItem(LANDLORD_TOKEN_KEY);
      if (stored) setPhase("signed_in");
      else setPhase("request");
      return;
    }
    // If we already hold a valid session token, honour it — magic-link URLs are
    // single-use so a page retry with the same URL should not blow the session away.
    const stored = localStorage.getItem(LANDLORD_TOKEN_KEY);
    if (stored) {
      setPhase("signed_in");
      nav("/landlord", { replace: true });
      return;
    }
    if (verifiedRef.current === urlToken) return;
    verifiedRef.current = urlToken;
    (async () => {
      try {
        const { data: res } = await axios.post(`${API}/landlord/verify`, { token: urlToken });
        localStorage.setItem(LANDLORD_TOKEN_KEY, res.access_token);
        setLandlord(res.landlord);
        toast.success(`Welcome, ${res.landlord.owner_name || "landlord"}`);
        setPhase("signed_in");
        nav("/landlord", { replace: true });
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Link invalid or expired");
        setPhase("request");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlToken]);

  // 2) When signed_in — fetch profile + payouts
  useEffect(() => {
    if (phase !== "signed_in") return;
    const api = landlordClient();
    api.get("/landlord/me")
      .then((r) => setLandlord(r.data))
      .catch(() => {
        localStorage.removeItem(LANDLORD_TOKEN_KEY);
        setPhase("request");
      });
    api.get("/landlord/bookings").then((r) => setBookings(r.data)).catch(() => setBookings([]));
    api.get("/landlord/documents").then((r) => setDocs(r.data)).catch(() => setDocs([]));
    api.get("/landlord/tenants").then((r) => setTenants(r.data)).catch(() => setTenants([]));
    api.get(`/landlord/insights?months=${insightsMonths}`)
      .then((r) => setInsights(r.data))
      .catch(() => setInsights(null));
    axios.get(`${API}/public/portal-brand`).then((r) => setBrand(r.data)).catch(() => {});
    // Build last-12-months list
    const list = [];
    const now = new Date();
    for (let i = 0; i < 12; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      list.push(d.toISOString().slice(0, 7));
    }
    setMonths(list);
  }, [phase]);

  // 2b) Reload insights whenever the range changes
  useEffect(() => {
    if (phase !== "signed_in") return;
    const api = landlordClient();
    api.get(`/landlord/insights?months=${insightsMonths}`)
      .then((r) => setInsights(r.data))
      .catch(() => setInsights(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, insightsMonths]);

  // 2c) Rolling 6-month cleaning + bedding operational cost sparkline.
  useEffect(() => {
    if (phase !== "signed_in") return;
    const api = landlordClient();
    api.get("/landlord/op-cost-trend?months=6")
      .then((r) => setOpCostTrend(r.data))
      .catch(() => setOpCostTrend(null));
  }, [phase]);

  // 3) Fetch payout for selected month
  useEffect(() => {
    if (phase !== "signed_in" || !month) return;
    const api = landlordClient();
    setBusy(true);
    api.get("/landlord/payouts", { params: { month } })
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setBusy(false));
  }, [phase, month]);

  const requestLink = async (e) => {
    e.preventDefault();
    if (!email || !email.includes("@")) {
      toast.error("Enter your owner email address");
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/landlord/request-link`, { email });
      setSentTo(email);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong");
    } finally {
      setBusy(false);
    }
  };

  const download = async (fmt) => {
    const api = landlordClient();
    try {
      const res = await api.get(`/landlord/payouts.${fmt}`, {
        params: { month },
        responseType: "blob",
      });
      const mime = fmt === "csv" ? "text/csv" : "application/pdf";
      const blob = new Blob([res.data], { type: mime });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `newport-stays-payout-${month}.${fmt}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`${fmt.toUpperCase()} downloaded`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Download failed");
    }
  };

  const signOut = () => {
    localStorage.removeItem(LANDLORD_TOKEN_KEY);
    setLandlord(null);
    setData(null);
    setBookings([]);
    setDocs([]);
    setTenants([]);
    setPhase("request");
    setSentTo("");
  };

  const openTenantModal = (mode, tenant = null) => {
    setTenantModal({
      mode,
      tenant: tenant
        ? { ...tenant }
        : { tenant_name: "", tenant_email: "", property_id: "", address: "", monthly_rent: 0, notes: "" },
    });
  };

  const saveTenant = async () => {
    const t = tenantModal.tenant;
    if (!t.tenant_name?.trim()) {
      toast.error("Tenant name is required");
      return;
    }
    try {
      const client = landlordClient();
      const payload = {
        tenant_name: t.tenant_name.trim(),
        tenant_email: t.tenant_email || "",
        property_id: t.property_id || null,
        address: t.address || "",
        monthly_rent: Number(t.monthly_rent) || 0,
        notes: t.notes || "",
      };
      if (tenantModal.mode === "edit") {
        const { data: updated } = await client.patch(`/landlord/tenants/${t.id}`, payload);
        setTenants((ts) => ts.map((x) => (x.id === updated.id ? { ...x, ...updated } : x)));
        toast.success("Tenant updated");
      } else {
        const { data: created } = await client.post("/landlord/tenants", payload);
        setTenants((ts) => [created, ...ts]);
        toast.success("Tenant added");
      }
      setTenantModal(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    }
  };

  const deleteTenant = async (id) => {
    if (!window.confirm("Delete this tenant and all their payment records?")) return;
    try {
      const client = landlordClient();
      await client.delete(`/landlord/tenants/${id}`);
      setTenants((ts) => ts.filter((x) => x.id !== id));
      if (expandedTenant === id) setExpandedTenant(null);
      toast.success("Tenant deleted");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  const toggleTenant = async (id) => {
    if (expandedTenant === id) {
      setExpandedTenant(null);
      return;
    }
    setExpandedTenant(id);
    try {
      const client = landlordClient();
      const { data } = await client.get(`/landlord/tenants/${id}/payments`);
      setTenantPayments(data);
    } catch {
      setTenantPayments([]);
    }
  };

  const recordPayment = async (tenantId) => {
    if (!paymentForm.paid_on || !paymentForm.amount) {
      toast.error("Date and amount are required");
      return;
    }
    try {
      const client = landlordClient();
      const { data } = await client.post(`/landlord/tenants/${tenantId}/payments`, {
        paid_on: paymentForm.paid_on,
        amount: Number(paymentForm.amount),
        method: paymentForm.method || "",
        notes: paymentForm.notes || "",
      });
      setTenantPayments((p) => [data, ...p]);
      setPaymentForm({ paid_on: new Date().toISOString().slice(0, 10), amount: "", method: "", notes: "" });
      // Refresh tenants for updated YTD totals
      const list = await client.get("/landlord/tenants").then((r) => r.data);
      setTenants(list);
      toast.success("Payment recorded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    }
  };

  const deletePayment = async (tenantId, paymentId) => {
    if (!window.confirm("Delete this payment record?")) return;
    try {
      const client = landlordClient();
      await client.delete(`/landlord/tenants/${tenantId}/payments/${paymentId}`);
      setTenantPayments((p) => p.filter((x) => x.id !== paymentId));
      const list = await client.get("/landlord/tenants").then((r) => r.data);
      setTenants(list);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  useEffect(() => {
    if (phase === "signed_in" && landlord) {
      loadStatementPrefs();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, landlord]);

  const emailAllStatements = async () => {
    if (!window.confirm("Email every tenant with an email their statement now?")) return;
    // Session Z45 — stream progress via SSE so the landlord sees live
    // "7 of 12 sent" instead of waiting for the whole batch.
    const token = localStorage.getItem(LANDLORD_TOKEN_KEY);
    setSendAllProgress({ sent: 0, failed: 0, total: 0, index: 0, tenant_name: "" });
    try {
      const res = await fetch(`${API}/landlord/statements/email-all-stream`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({}),
      });
      if (!res.ok || !res.body) throw new Error("Send-all stream failed to open");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let final = null;
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const chunks = buffer.split("\n\n");
        buffer = chunks.pop();
        for (const chunk of chunks) {
          const line = chunk.replace(/^data: /, "").trim();
          if (!line) continue;
          try {
            const evt = JSON.parse(line);
            if (evt.event === "progress") {
              setSendAllProgress({
                sent: evt.sent, failed: evt.failed,
                total: evt.total, index: evt.index,
                tenant_name: evt.tenant_name || "",
              });
            } else if (evt.event === "done") {
              final = evt;
              setSendAllProgress({ ...evt, index: evt.total, tenant_name: "" });
            } else if (evt.event === "start") {
              setSendAllProgress({ sent: 0, failed: 0, total: evt.total, index: 0, tenant_name: "" });
            }
          } catch { /* ignore malformed event */ }
        }
      }
      if (final) {
        toast.success(
          `Sent ${final.sent} of ${final.total} statements` +
          (final.failed ? ` · ${final.failed} failed` : ""),
        );
      }
    } catch (err) {
      toast.error(err?.message || "Send-all failed");
    } finally {
      setTimeout(() => setSendAllProgress(null), 4000);
    }
  };

  const downloadStatement = async (tenant) => {
    try {
      const client = landlordClient();
      const res = await client.get(`/landlord/tenants/${tenant.id}/statement.pdf`, {
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `rent-statement-${(tenant.tenant_name || "tenant").replace(/\s+/g, "-").toLowerCase()}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Statement PDF downloaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Download failed");
    }
  };

  const previewCostHistoryPdf = async (tenant) => {
    // Session Z43 — preview the same cost-history PDF that will auto-attach.
    if (!tenant?.property_id) {
      toast.error("Tenant has no linked property to preview");
      return;
    }
    try {
      const client = landlordClient();
      const res = await client.get(
        `/landlord/properties/${tenant.property_id}/cleaning-cost-history.pdf`,
        { responseType: "blob" },
      );
      const url = window.URL.createObjectURL(
        new Blob([res.data], { type: "application/pdf" }),
      );
      window.open(url, "_blank", "noopener");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Preview failed");
    }
  };

  const loadStatementPrefs = async () => {
    try {
      const client = landlordClient();
      const { data } = await client.get("/landlord/statement-prefs");
      setStatementPrefs(data);
    } catch { /* silent */ }
  };

  const saveStatementPrefs = async (next) => {
    setPrefsBusy(true);
    try {
      const client = landlordClient();
      const { data } = await client.put("/landlord/statement-prefs", next);
      setStatementPrefs(data);
      toast.success("Statement preferences saved");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setPrefsBusy(false);
    }
  };

  // Also fetch brand on the public "request-link" page so it looks right before sign-in
  useEffect(() => {
    axios.get(`${API}/public/portal-brand`).then((r) => setBrand(r.data)).catch(() => {});
  }, []);

  const uploadDoc = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 20 * 1024 * 1024) {
      toast.error("File must be under 20 MB");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const client = landlordClient();
      const params = new URLSearchParams({ kind: docKind });
      if (docExpiry) params.set("expires_on", docExpiry);
      const { data: res } = await client.post(`/landlord/documents?${params.toString()}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setDocs((d) => [res, ...d]);
      setDocExpiry("");
      toast.success("Document uploaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const saveExpiry = async () => {
    if (!editingExpiry) return;
    try {
      const client = landlordClient();
      const { data: res } = await client.patch(`/landlord/documents/${editingExpiry.id}`, {
        expires_on: editingExpiry.expires_on || "",
      });
      setDocs((ds) => ds.map((d) => (d.id === res.id ? res : d)));
      setEditingExpiry(null);
      toast.success("Expiry updated");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed");
    }
  };

  const openBooking = async (id) => {
    setLoadingBooking(true);
    setBookingDetail({ id });
    try {
      const client = landlordClient();
      const { data: b } = await client.get(`/landlord/bookings/${id}`);
      setBookingDetail(b);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Load failed");
      setBookingDetail(null);
    } finally {
      setLoadingBooking(false);
    }
  };

  const deleteDoc = async (id) => {
    if (!window.confirm("Delete this document?")) return;
    try {
      const client = landlordClient();
      await client.delete(`/landlord/documents/${id}`);
      setDocs((d) => d.filter((x) => x.id !== id));
      toast.success("Deleted");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  const kindLabel = (k) => ({
    lease: "Lease / tenancy",
    epc: "EPC certificate",
    gas_cert: "Gas safety",
    insurance: "Insurance",
    other: "Other document",
  })[k] || "Document";

  const fmtDate = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
    } catch {
      return iso;
    }
  };

  const monthLabel = (iso) => {
    const [y, m] = iso.split("-");
    return new Date(Number(y), Number(m) - 1, 1).toLocaleString("en-GB", {
      month: "long",
      year: "numeric",
    });
  };

  // ---------- Views ----------
  if (phase === "boot") {
    return (
      <div className="min-h-screen flex items-center justify-center text-slate-500 font-serif tracking-wide bg-stone-50">
        Signing you in…
      </div>
    );
  }

  if (phase === "request") {
    return (
      <div className="min-h-screen grid lg:grid-cols-[1.05fr_1fr] bg-stone-50">
        <div className="ns-login-hero hidden lg:flex flex-col justify-between p-14 text-white">
          <div className="flex items-center gap-4">
            <img
              src={LOGO_URL}
              alt="Newport Stays"
              className="w-14 h-14 rounded-xl object-cover ring-1 ring-white/30"
            />
            <div>
              <div className="font-serif text-2xl">{brand.brand_name}</div>
              <div className="text-[11px] tracking-[0.22em] uppercase text-teal-200/90">
                {brand.brand_tagline}
              </div>
            </div>
          </div>
          <div className="max-w-md">
            <div className="ns-label mb-4 text-teal-200/80">Your monthly payouts, one click away.</div>
            <h2 className="font-serif text-5xl leading-tight">Owner portal</h2>
            <p className="mt-6 text-white/80 leading-relaxed">
              Sign in with the email {brand.brand_name} has on file for your property.
              We&apos;ll send you a secure one-tap link — no password, no fuss.
            </p>
          </div>
          <div className="text-xs text-white/40 tracking-widest uppercase flex items-center justify-between gap-4">
            <div>
              © {new Date().getFullYear()} {brand.brand_name}
              {brand.powered_by_visible && brand.brand_name !== "Newport Stays" && (
                <div className="mt-1 normal-case tracking-normal text-white/30">
                  Powered by Newport Stays
                </div>
              )}
            </div>
            <DarkModeToggle className="!bg-white/10 !border-white/20 !text-white hover:!bg-white/20 hover:!border-white/40 hover:!text-white" />
          </div>
        </div>

        <div className="flex items-center justify-center p-8">
          <div className="w-full max-w-md bg-white border border-slate-200/80 rounded-2xl p-10 shadow-[0_20px_60px_rgba(10,17,40,0.06)]">
            {sentTo ? (
              <div className="text-center">
                <div className="w-14 h-14 rounded-full bg-teal-50 text-teal-700 flex items-center justify-center mx-auto mb-6">
                  <Mail className="w-6 h-6" />
                </div>
                <h1 className="font-serif text-3xl text-slate-900">Check your email</h1>
                <p className="mt-3 text-sm text-slate-500">
                  If <span className="font-medium text-slate-700">{sentTo}</span> is an
                  owner email on Newport Stays, we&apos;ve just sent a secure sign-in
                  link. It expires in 24 hours.
                </p>
                <button
                  onClick={() => { setSentTo(""); setEmail(""); }}
                  className="mt-8 text-xs text-slate-400 hover:text-slate-600 tracking-wider"
                >
                  ← Use a different email
                </button>
              </div>
            ) : (
              <form onSubmit={requestLink}>
                <div className="ns-label mb-2">Owner portal</div>
                <h1 className="font-serif text-3xl md:text-4xl text-slate-900">
                  Send me a sign-in link
                </h1>
                <p className="mt-2 text-sm text-slate-500">
                  Enter the email Newport Stays has on file for you.
                </p>
                <div className="mt-8 space-y-5">
                  <div>
                    <Label className="ns-label">Owner email</Label>
                    <Input
                      data-testid="landlord-email-input"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="mt-2 h-11 bg-white"
                      placeholder="jane@example.com"
                      autoComplete="email"
                      required
                    />
                  </div>
                  <Button
                    data-testid="landlord-request-link-btn"
                    disabled={busy}
                    className="w-full h-11 rounded-full ns-btn-primary text-white text-sm tracking-wide"
                    type="submit"
                  >
                    {busy ? "Sending…" : "Email me a sign-in link"}
                  </Button>
                </div>
                <div className="mt-8 text-xs text-slate-400 text-center tracking-wider inline-flex items-center gap-2 justify-center w-full">
                  <ShieldCheck className="w-3.5 h-3.5" />
                  Secure magic-link login · No password required
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    );
  }

  // signed_in
  const t = data?.totals || {};
  return (
    <div className="min-h-screen bg-stone-50">
      <header className="bg-[#0a1128] text-white px-6 md:px-12 py-6 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <img
            src={LOGO_URL}
            className="w-10 h-10 rounded-lg object-cover ring-1 ring-white/20"
            alt=""
          />
          <div>
            <div className="font-serif text-xl">{brand.brand_name}</div>
            <div className="text-[10px] tracking-[0.22em] uppercase text-teal-200/80">
              {brand.brand_tagline} · Read-only
            </div>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-right hidden sm:block">
            <div className="text-sm">{landlord?.owner_name || "Owner"}</div>
            <div className="text-[11px] text-slate-400">{landlord?.email}</div>
          </div>
          <DarkModeToggle className="!bg-white/10 !border-white/20 !text-white hover:!bg-white/20 hover:!border-white/40 hover:!text-white" />
          <button
            data-testid="landlord-signout-btn"
            onClick={signOut}
            className="p-2 rounded-lg text-slate-300 hover:text-white hover:bg-white/10"
            title="Sign out"
          >
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 md:px-10 py-10">
        <div className="ns-label mb-2">Your payouts</div>
        <h1 className="font-serif text-4xl md:text-5xl text-slate-900 tracking-tight">
          {landlord?.owner_name ? `Hello, ${landlord.owner_name.split(" ")[0]}` : "Your payouts"}
        </h1>
        <p className="mt-2 text-slate-500 max-w-2xl">
          Read-only summary of your monthly earnings, grouped by unit. Choose a month and
          download the PDF or CSV whenever you need it.
        </p>

        <div className="mt-8 flex items-center gap-3 flex-wrap">
          <select
            data-testid="landlord-month-select"
            className="h-11 rounded-md border border-slate-200 bg-white px-4 text-sm min-w-[10rem]"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
          >
            {months.map((m) => (
              <option key={m} value={m}>{monthLabel(m)}</option>
            ))}
          </select>
          <Button
            data-testid="landlord-download-pdf"
            variant="outline"
            onClick={() => download("pdf")}
            disabled={busy || !data?.rows?.length}
            className="rounded-full h-11 px-5"
          >
            <FileText className="w-4 h-4 mr-2" /> Download PDF
          </Button>
          <Button
            data-testid="landlord-download-csv"
            onClick={() => download("csv")}
            disabled={busy || !data?.rows?.length}
            className="rounded-full ns-btn-primary h-11 px-6"
          >
            <Download className="w-4 h-4 mr-2" /> Download CSV
          </Button>
        </div>

        <div className="mt-8 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {[
            { label: "Bookings", value: t.bookings ?? 0 },
            { label: "Nights", value: t.nights ?? 0 },
            { label: "Gross", value: money(t.gross), tone: "teal" },
            { label: "Cleaning cost", value: money(t.cleaning_cost ?? 0), tone: "muted", testid: "landlord-headline-cleaning-cost" },
            { label: "Bedding", value: money(t.bedding_cost ?? 0), tone: "muted", testid: "landlord-headline-bedding-cost" },
            { label: "Net payout", value: money(t.net), tone: "navy" },
          ].map((s) => (
            <div key={s.label} className="ns-card p-5" data-testid={s.testid}>
              <div className="ns-label">{s.label}</div>
              <div className={`font-serif text-2xl lg:text-3xl mt-3 ${
                s.tone === "teal" ? "text-teal-800" :
                s.tone === "muted" ? "text-slate-600" : "text-slate-900"}`}>
                {s.value}
              </div>
            </div>
          ))}
        </div>

        {opCostTrend && opCostTrend.series?.length > 0 && (
          <div className="mt-8 ns-card p-5" data-testid="landlord-opcost-trend">
            <div className="flex items-center gap-3 mb-4 flex-wrap">
              <Receipt className="w-5 h-5 text-indigo-700" />
              <div className="flex-1 min-w-0">
                <div className="ns-label">Operational cost trend</div>
                <div className="text-xs text-slate-400 mt-1">
                  Rolling 6-month view of cleaning + bedding spend across your units — the ops split behind every payout.
                </div>
              </div>
              <div className="text-right shrink-0">
                <div className="text-[11px] uppercase tracking-wider text-slate-400">Last 6 months</div>
                <div className="font-serif text-xl text-slate-900 tabular-nums" data-testid="landlord-opcost-total">
                  {money(opCostTrend.series.reduce((a, s) => a + (s.total || 0), 0))}
                </div>
              </div>
            </div>
            <div className="h-56" data-testid="landlord-opcost-chart">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={opCostTrend.series} margin={{ top: 10, right: 16, bottom: 8, left: 0 }}>
                  <CartesianGrid stroke="#e2e8f0" strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="month_label" tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} />
                  <YAxis tickFormatter={(v) => `£${v.toFixed(0)}`} tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} width={54} />
                  <Tooltip
                    contentStyle={{ borderRadius: 8, borderColor: "#e2e8f0", fontSize: 12 }}
                    formatter={(value, name) => [`£${(value || 0).toFixed(2)}`, name]}
                  />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Line type="monotone" dataKey="cleaning_cost" name="Cleaning" stroke="#0d7a75" strokeWidth={2.5} dot={{ r: 3 }} isAnimationActive={false} />
                  <Line type="monotone" dataKey="bedding_cost" name="Bedding" stroke="#4f46e5" strokeWidth={2.5} dot={{ r: 3 }} isAnimationActive={false} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        <div className="mt-10 ns-card">
          <div className="p-5 flex items-center gap-3 border-b border-slate-100">
            <CalendarClock className="w-5 h-5 text-teal-700" />
            <div>
              <div className="ns-label">Upcoming stays</div>
              <div className="text-xs text-slate-400 mt-1">
                Confirmed reservations for your units — guest names are shown as first-name only for privacy.
              </div>
            </div>
          </div>
          {bookings.length === 0 ? (
            <div className="text-center py-10 text-slate-400 text-sm italic">
              No upcoming stays right now.
            </div>
          ) : (
            <table className="min-w-full text-sm">
              <thead className="bg-stone-50">
                <tr>
                  <th className="text-left px-6 py-3 ns-label">Guest</th>
                  <th className="text-left px-4 py-3 ns-label">Property</th>
                  <th className="text-left px-4 py-3 ns-label">Check-in</th>
                  <th className="text-left px-4 py-3 ns-label">Check-out</th>
                  <th className="text-center px-4 py-3 ns-label">Guests</th>
                  <th className="text-left px-4 py-3 ns-label">Channel</th>
                </tr>
              </thead>
              <tbody>
                {bookings.slice(0, 20).map((b) => (
                  <tr
                    key={b.id}
                    data-testid={`landlord-booking-${b.id}`}
                    onClick={() => openBooking(b.id)}
                    className="border-t border-slate-100 cursor-pointer hover:bg-teal-50/30 transition-colors"
                  >
                    <td className="px-6 py-4 font-medium">{b.guest_name}</td>
                    <td className="px-4 py-4 text-slate-600">{b.property_name}</td>
                    <td className="px-4 py-4">{fmtDate(b.check_in)}</td>
                    <td className="px-4 py-4">{fmtDate(b.check_out)}</td>
                    <td className="px-4 py-4 text-center">{b.guests_count}</td>
                    <td className="px-4 py-4 text-xs text-slate-500 uppercase tracking-wider">
                      {b.channel?.replace(/official/i, "").trim() || "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="mt-8 ns-card">
          <div className="p-5 flex items-center justify-between border-b border-slate-100">
            <div className="flex items-center gap-3">
              <Paperclip className="w-5 h-5 text-teal-700" />
              <div>
                <div className="ns-label">Your documents</div>
                <div className="text-xs text-slate-400 mt-1">
                  Leases, EPCs, gas safety certs — everything in one tidy owner file.
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap justify-end">
              <select
                data-testid="landlord-doc-kind"
                className="h-10 rounded-md border border-slate-200 bg-white px-3 text-xs"
                value={docKind}
                onChange={(e) => setDocKind(e.target.value)}
              >
                <option value="lease">Lease / tenancy</option>
                <option value="epc">EPC certificate</option>
                <option value="gas_cert">Gas safety</option>
                <option value="insurance">Insurance</option>
                <option value="other">Other</option>
              </select>
              {(docKind === "epc" || docKind === "gas_cert" || docKind === "insurance") && (
                <Input
                  data-testid="landlord-doc-expiry"
                  type="date"
                  value={docExpiry}
                  onChange={(e) => setDocExpiry(e.target.value)}
                  className="h-10 w-40 bg-white text-xs"
                  title="Optional — expiry date so we can nudge you 30 days before"
                />
              )}
              <label className="inline-flex items-center gap-2 h-10 px-4 rounded-full bg-teal-700 hover:bg-teal-800 text-white text-xs cursor-pointer tracking-wider">
                <Upload className="w-3.5 h-3.5" />
                {uploading ? "Uploading…" : "Upload"}
                <input
                  type="file"
                  data-testid="landlord-doc-upload"
                  accept="application/pdf,image/jpeg,image/png,image/webp,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                  className="hidden"
                  disabled={uploading}
                  onChange={uploadDoc}
                />
              </label>
            </div>
          </div>
          {docs.length === 0 ? (
            <div className="text-center py-10 text-slate-400 text-sm italic">
              No documents yet — upload your first lease, EPC or gas safety certificate.
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {docs.map((d) => {
                const hasExpiry = ["epc", "gas_cert", "insurance"].includes(d.kind);
                const expDate = d.expires_on ? new Date(d.expires_on) : null;
                const today = new Date(); today.setHours(0, 0, 0, 0);
                const days = expDate ? Math.round((expDate - today) / 86400000) : null;
                const chipCls = days === null
                  ? "bg-slate-100 text-slate-500"
                  : days < 0
                    ? "bg-red-50 text-red-700 border-red-200"
                    : days <= 30
                      ? "bg-amber-50 text-amber-700 border-amber-200"
                      : "bg-teal-50 text-teal-700 border-teal-200";
                const chipLabel = days === null
                  ? "No expiry set"
                  : days < 0
                    ? `Expired ${Math.abs(days)}d ago`
                    : days === 0
                      ? "Expires today"
                      : `Expires in ${days}d`;
                return (
                  <div
                    key={d.id}
                    data-testid={`landlord-doc-${d.id}`}
                    className="px-6 py-4 flex items-center gap-4 hover:bg-stone-50"
                  >
                    <FileText className="w-5 h-5 text-slate-400 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{d.filename}</div>
                      <div className="text-[11px] text-slate-400 mt-0.5">
                        {kindLabel(d.kind)} · uploaded {fmtDate(d.uploaded_at)} · {(d.size / 1024).toFixed(0)} KB
                      </div>
                    </div>
                    {hasExpiry && (
                      <button
                        onClick={() => setEditingExpiry({ id: d.id, expires_on: d.expires_on || "" })}
                        className={`text-[11px] tracking-wider px-2.5 py-1 rounded-full border ${chipCls} inline-flex items-center gap-1 hover:opacity-80`}
                        title="Edit expiry date"
                      >
                        {days !== null && days <= 30 && <AlertTriangle className="w-3 h-3" />}
                        {chipLabel}
                        <Pencil className="w-3 h-3 opacity-50" />
                      </button>
                    )}
                    <a
                      href={d.url}
                      target="_blank"
                      rel="noreferrer"
                      className="text-teal-700 hover:text-teal-900 text-xs tracking-wider inline-flex items-center gap-1"
                    >
                      <Download className="w-3.5 h-3.5" /> Open
                    </a>
                    <button
                      onClick={() => deleteDoc(d.id)}
                      className="text-slate-300 hover:text-red-600 p-1"
                      title="Delete"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {insights && insights.property_count > 0 && (
          <div className="mt-8 ns-card p-5" data-testid="landlord-insights-card">
            <div className="flex items-center gap-3 mb-5 flex-wrap">
              <TrendingUp className="w-5 h-5 text-teal-700" />
              <div className="flex-1 min-w-0">
                <div className="ns-label">Occupancy &amp; revenue trend</div>
                <div className="text-xs text-slate-400 mt-1">
                  Rolling {insights.months}-month view across all your units — spot slow months before they arrive.
                </div>
              </div>
              <select
                data-testid="landlord-insights-range"
                className="h-9 rounded-md border border-slate-200 bg-white px-3 text-xs"
                value={insightsMonths}
                onChange={(e) => setInsightsMonths(Number(e.target.value))}
              >
                <option value={3}>Last 3 months</option>
                <option value={6}>Last 6 months</option>
                <option value={12}>Last 12 months</option>
              </select>
            </div>
            <div className="h-72" data-testid="landlord-insights-chart">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={insights.totals} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
                  <CartesianGrid stroke="#e2e8f0" strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="month_label" tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} />
                  <YAxis yAxisId="occ" domain={[0, 100]} unit="%" tick={{ fontSize: 11, fill: "#0d7a75" }} axisLine={false} tickLine={false} width={44} />
                  <YAxis yAxisId="rev" orientation="right" tickFormatter={(v) => `£${v.toFixed(0)}`} tick={{ fontSize: 11, fill: "#a16207" }} axisLine={false} tickLine={false} width={54} />
                  <Tooltip
                    contentStyle={{ borderRadius: 8, borderColor: "#e2e8f0", fontSize: 12 }}
                    formatter={(value, name) => {
                      if (name === "Occupancy") return [`${value}%`, name];
                      if (name === "Occupancy (last year)") return [`${value}%`, name];
                      if (name === "Revenue") return [`£${(value || 0).toFixed(2)}`, name];
                      if (name === "Revenue (last year)") return [`£${(value || 0).toFixed(2)}`, name];
                      return [value, name];
                    }}
                  />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Line yAxisId="occ" type="monotone" dataKey="prior_year_occupancy_pct" name="Occupancy (last year)" stroke="#0d7a75" strokeOpacity={0.35} strokeWidth={1.5} strokeDasharray="2 4" dot={false} isAnimationActive={false} />
                  <Line yAxisId="rev" type="monotone" dataKey="prior_year_revenue" name="Revenue (last year)" stroke="#b45309" strokeOpacity={0.35} strokeWidth={1.5} strokeDasharray="2 4" dot={false} isAnimationActive={false} />
                  <Line yAxisId="occ" type="monotone" dataKey="occupancy_pct" name="Occupancy" stroke="#0d7a75" strokeWidth={2.5} dot={{ r: 4 }} activeDot={{ r: 6 }} />
                  <Line yAxisId="rev" type="monotone" dataKey="revenue" name="Revenue" stroke="#b45309" strokeWidth={2.5} strokeDasharray="4 3" dot={{ r: 4 }} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
            {insights.per_property.length > 1 && (
              <div className="mt-4 grid gap-2 text-xs" data-testid="landlord-insights-perunit">
                {insights.per_property.map((prop) => {
                  const last = prop.series[prop.series.length - 1] || {};
                  return (
                    <button
                      key={prop.property_id}
                      type="button"
                      data-testid={`landlord-insights-unit-${prop.property_id}`}
                      onClick={() => {
                        setHighlightPropId(prop.property_id);
                        setTimeout(() => {
                          const el = perUnitRowRefs.current[prop.property_id];
                          if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
                        }, 30);
                        setTimeout(() => setHighlightPropId(null), 3000);
                      }}
                      className="w-full flex items-center gap-3 rounded-lg bg-stone-50 px-3 py-2 hover:bg-teal-50/60 hover:ring-1 hover:ring-teal-200 transition text-left"
                      title="Scroll to this unit's payout row"
                    >
                      <Building2 className="w-3.5 h-3.5 text-teal-700 shrink-0" />
                      <div className="flex-1 min-w-0 truncate font-medium">{prop.property_name}</div>
                      <div className="text-teal-800">{last.occupancy_pct ?? 0}% occ</div>
                      <div className="text-amber-700 w-20 text-right">
                        £{Number(last.revenue || 0).toFixed(0)}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}

        <LandlordProfitSection client={landlordClient} />

        <div className="mt-8 ns-card overflow-x-auto">
          <div className="p-5 flex items-center gap-3">
            <BarChart3 className="w-5 h-5 text-teal-700" />
            <div>
              <div className="ns-label">
                Per unit &middot; {data?.month || monthLabel(month)}
              </div>
              <div className="text-xs text-slate-400 mt-1 inline-flex items-center gap-1.5">
                <Building2 className="w-3 h-3" />
                {landlord?.property_count || 0} propert{landlord?.property_count === 1 ? "y" : "ies"} on file
              </div>
            </div>
          </div>
          <table className="min-w-full text-sm">
            <thead className="bg-stone-50">
              <tr>
                <th className="text-left px-6 py-3 ns-label">Property</th>
                <th className="text-right px-4 py-3 ns-label">Bookings</th>
                <th className="text-right px-4 py-3 ns-label">Nights</th>
                <th className="text-right px-4 py-3 ns-label">Gross</th>
                <th className="text-right px-4 py-3 ns-label" title="Cleaning fee charged on direct bookings">Cleaning fee</th>
                <th className="text-right px-4 py-3 ns-label" title="Operational cleaning cost: your per-turnover rate × turnovers">Cleaning cost</th>
                <th className="text-right px-4 py-3 ns-label" title="Linen & towel hire cost (Johnsons) × turnovers">Bedding</th>
                <th className="text-right px-4 py-3 ns-label">Commission</th>
                <th className="text-right px-4 py-3 ns-label">Refunded</th>
                <th className="text-right px-6 py-3 ns-label">Net payout</th>
              </tr>
            </thead>
            <tbody>
              {(data?.rows || []).map((r) => (
                <tr
                  key={r.property_id}
                  ref={(el) => { perUnitRowRefs.current[r.property_id] = el; }}
                  data-testid={`landlord-row-${r.property_id}`}
                  className={`border-t border-slate-100 transition-colors ${r.bookings === 0 ? "opacity-50" : ""} ${
                    highlightPropId === r.property_id ? "bg-amber-50/70 ring-1 ring-inset ring-amber-200" : ""
                  }`}
                >
                  <td className="px-6 py-4 font-medium">{r.property_name}</td>
                  <td className="px-4 py-4 text-right">{r.bookings}</td>
                  <td className="px-4 py-4 text-right">{r.nights}</td>
                  <td className="px-4 py-4 text-right">{money(r.gross)}</td>
                  <td className="px-4 py-4 text-right">{money(r.cleaning)}</td>
                  <td className="px-4 py-4 text-right text-slate-600" data-testid={`landlord-cleaning-cost-${r.property_id}`}>{money(r.cleaning_cost)}</td>
                  <td className="px-4 py-4 text-right text-slate-600" data-testid={`landlord-bedding-cost-${r.property_id}`}>{money(r.bedding_cost)}</td>
                  <td className="px-4 py-4 text-right text-amber-700">{money(r.commission)}</td>
                  <td className="px-4 py-4 text-right text-red-600">
                    {r.refunded > 0 ? money(r.refunded) : "—"}
                  </td>
                  <td className="px-6 py-4 text-right font-medium">{money(r.net)}</td>
                </tr>
              ))}
              {(!data || data.rows?.length === 0) && (
                <tr>
                  <td colSpan="10" className="text-center py-12 text-slate-400 text-sm italic">
                    {busy ? "Loading…" : "No bookings for this month."}
                  </td>
                </tr>
              )}
            </tbody>
            {data?.rows?.length > 0 && (
              <tfoot>
                <tr className="border-t-2 border-slate-200 bg-stone-50/60">
                  <td className="px-6 py-3 font-serif text-lg">Total</td>
                  <td className="px-4 py-3 text-right font-medium">{t.bookings}</td>
                  <td className="px-4 py-3 text-right font-medium">{t.nights}</td>
                  <td className="px-4 py-3 text-right font-medium">{money(t.gross)}</td>
                  <td className="px-4 py-3 text-right font-medium">{money(t.cleaning)}</td>
                  <td className="px-4 py-3 text-right font-medium text-slate-600" data-testid="landlord-total-cleaning-cost">{money(t.cleaning_cost)}</td>
                  <td className="px-4 py-3 text-right font-medium text-slate-600" data-testid="landlord-total-bedding-cost">{money(t.bedding_cost)}</td>
                  <td className="px-4 py-3 text-right font-medium text-amber-700">
                    {money(t.commission)}
                  </td>
                  <td className="px-4 py-3 text-right font-medium text-red-600">
                    {t.refunded > 0 ? money(t.refunded) : "—"}
                  </td>
                  <td className="px-6 py-3 text-right font-serif text-lg text-slate-900">
                    {money(t.net)}
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>

        <div className="mt-8 ns-card">
          <div className="p-5 flex items-center justify-between border-b border-slate-100 flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <Users className="w-5 h-5 text-teal-700" />
              <div>
                <div className="ns-label">Your tenants</div>
                <div className="text-xs text-slate-400 mt-1">
                  Track rent payments and generate an HMRC-ready statement PDF per tenant.
                </div>
              </div>
            </div>
            <Button
              data-testid="landlord-tenant-add"
              onClick={() => openTenantModal("new")}
              className="rounded-full ns-btn-primary h-10 px-5 text-xs"
            >
              <Plus className="w-3.5 h-3.5 mr-1.5" /> Add tenant
            </Button>
          </div>
          <div className="px-5 py-3 flex items-center justify-between gap-3 border-b border-slate-100 bg-stone-50/40 flex-wrap">
            <label
              className="inline-flex items-center gap-2 text-[11px] text-slate-600 cursor-pointer select-none"
              data-testid="landlord-statement-prefs-toggle"
              title="When on, every tenant statement email auto-attaches the branded cleaning-cost history PDF"
            >
              <input
                type="checkbox"
                checked={statementPrefs.auto_attach_cost_history}
                disabled={prefsBusy}
                onChange={(e) =>
                  saveStatementPrefs({ auto_attach_cost_history: e.target.checked })
                }
                className="accent-teal-700"
              />
              Auto-attach cost history PDF to statement emails
            </label>
            <button
              type="button"
              data-testid="landlord-statements-email-all"
              onClick={emailAllStatements}
              disabled={tenants.length === 0 || !!sendAllProgress}
              className="text-[11px] rounded-full bg-teal-700 text-white px-3 py-1.5 hover:bg-teal-800 transition disabled:opacity-40"
              title="Email every tenant with an email their statement now"
            >
              {sendAllProgress
                ? `Sending ${sendAllProgress.index}/${sendAllProgress.total}${sendAllProgress.tenant_name ? ` · ${sendAllProgress.tenant_name}` : ""}`
                : "✉ Email all statements"}
            </button>
          </div>
          {tenants.length === 0 ? (
            <div className="text-center py-10 text-slate-400 text-sm italic">
              No tenants yet — add your first tenant to start tracking rent for HMRC.
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {tenants.map((t) => {
                const propName = landlord?.properties?.find((p) => p.id === t.property_id)?.name;
                return (
                  <div key={t.id} data-testid={`landlord-tenant-${t.id}`}>
                    <div className="px-6 py-4 flex items-center gap-4 hover:bg-stone-50">
                      <button
                        onClick={() => toggleTenant(t.id)}
                        className="flex-1 min-w-0 text-left"
                      >
                        <div className="text-sm font-medium truncate">{t.tenant_name}</div>
                        <div className="text-[11px] text-slate-400 mt-0.5">
                          {propName || t.address || "—"}
                          {t.monthly_rent > 0 && ` · ${money(t.monthly_rent)}/mo`}
                          {` · £${(t.ytd_total || 0).toFixed(2)} recorded this tax year (${t.ytd_count || 0} payment${t.ytd_count === 1 ? "" : "s"})`}
                        </div>
                      </button>
                      <button
                        data-testid={`landlord-tenant-statement-${t.id}`}
                        onClick={() => downloadStatement(t)}
                        className="text-teal-700 hover:text-teal-900 text-xs tracking-wider inline-flex items-center gap-1"
                        title="Download HMRC rent statement PDF"
                      >
                        <Receipt className="w-3.5 h-3.5" /> Statement
                      </button>
                      {statementPrefs.auto_attach_cost_history && t.property_id && (
                        <button
                          data-testid={`landlord-tenant-costhistory-preview-${t.id}`}
                          onClick={() => previewCostHistoryPdf(t)}
                          className="text-slate-400 hover:text-teal-700 text-[10px] tracking-wider inline-flex items-center gap-1"
                          title="Preview the cost history PDF that will auto-attach"
                        >
                          👁 Preview attachment
                        </button>
                      )}
                      <button
                        onClick={() => openTenantModal("edit", t)}
                        className="text-slate-300 hover:text-teal-700 p-1"
                        title="Edit"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => deleteTenant(t.id)}
                        className="text-slate-300 hover:text-red-600 p-1"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                    {expandedTenant === t.id && (
                      <div className="px-6 pb-6 bg-stone-50/60">
                        <div className="grid grid-cols-4 gap-2 items-end pt-4">
                          <div>
                            <Label className="text-[11px] text-slate-500">Date paid</Label>
                            <Input
                              data-testid={`payment-date-${t.id}`}
                              type="date"
                              value={paymentForm.paid_on}
                              onChange={(e) => setPaymentForm({ ...paymentForm, paid_on: e.target.value })}
                              className="mt-1 h-10 bg-white"
                            />
                          </div>
                          <div>
                            <Label className="text-[11px] text-slate-500">Amount £</Label>
                            <Input
                              data-testid={`payment-amount-${t.id}`}
                              type="number"
                              step="0.01"
                              value={paymentForm.amount}
                              onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })}
                              className="mt-1 h-10 bg-white"
                            />
                          </div>
                          <div>
                            <Label className="text-[11px] text-slate-500">Method</Label>
                            <Input
                              value={paymentForm.method}
                              onChange={(e) => setPaymentForm({ ...paymentForm, method: e.target.value })}
                              placeholder="Bank transfer"
                              className="mt-1 h-10 bg-white"
                            />
                          </div>
                          <Button
                            data-testid={`payment-save-${t.id}`}
                            onClick={() => recordPayment(t.id)}
                            className="rounded-full ns-btn-primary h-10 px-4 text-xs"
                          >
                            <Plus className="w-3.5 h-3.5 mr-1" /> Record payment
                          </Button>
                        </div>
                        {tenantPayments.length === 0 ? (
                          <div className="text-center py-6 text-slate-400 text-xs italic">No payments recorded yet.</div>
                        ) : (
                          <table className="mt-5 min-w-full text-xs">
                            <thead>
                              <tr className="text-slate-500">
                                <th className="text-left py-2">Date</th>
                                <th className="text-right py-2">Amount</th>
                                <th className="text-left py-2">Method</th>
                                <th className="text-left py-2">Notes</th>
                                <th></th>
                              </tr>
                            </thead>
                            <tbody>
                              {tenantPayments.map((p) => (
                                <tr key={p.id} className="border-t border-slate-100">
                                  <td className="py-2">{fmtDate(p.paid_on)}</td>
                                  <td className="py-2 text-right font-medium">{money(p.amount)}</td>
                                  <td className="py-2 text-slate-500">{p.method || "—"}</td>
                                  <td className="py-2 text-slate-500 truncate">{p.notes || "—"}</td>
                                  <td className="py-2 text-right">
                                    <button
                                      onClick={() => deletePayment(t.id, p.id)}
                                      className="text-slate-300 hover:text-red-600 p-1"
                                      title="Delete payment"
                                    >
                                      <Trash2 className="w-3.5 h-3.5" />
                                    </button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="mt-10 text-xs text-slate-400 text-center">
          Questions? Reply to your monthly payout email — we&apos;re here to help.
          {brand.powered_by_visible && brand.brand_name !== "Newport Stays" && (
            <div className="mt-3 text-[10px] tracking-wider uppercase text-slate-300">
              Powered by Newport Stays
            </div>
          )}
        </div>
      </main>

      <Dialog open={!!bookingDetail} onOpenChange={(v) => !v && setBookingDetail(null)}>
        <DialogContent className="bg-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Booking details</DialogTitle>
          </DialogHeader>
          {loadingBooking || !bookingDetail?.property_name ? (
            <div className="text-sm text-slate-500 py-8 text-center">Loading…</div>
          ) : (
            <div className="space-y-5 text-sm">
              <div>
                <div className="ns-label">{bookingDetail.property_name}</div>
                <div className="mt-1 font-serif text-xl text-slate-900">
                  {bookingDetail.guest_name} · {bookingDetail.guests_count} guest{bookingDetail.guests_count === 1 ? "" : "s"}
                </div>
                <div className="text-xs text-slate-400 mt-1 uppercase tracking-wider">
                  {bookingDetail.channel?.replace(/official/i, "").trim()}
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <div className="ns-label">Check-in</div>
                  <div className="mt-1">{fmtDate(bookingDetail.check_in)}</div>
                </div>
                <div>
                  <div className="ns-label">Check-out</div>
                  <div className="mt-1">{fmtDate(bookingDetail.check_out)}</div>
                </div>
                <div>
                  <div className="ns-label">Nights</div>
                  <div className="mt-1">{bookingDetail.nights}</div>
                </div>
              </div>
              <div className="ns-hairline" />
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-slate-500">Nightly rate</span>
                  <span className="font-medium">{money(bookingDetail.nightly_rate)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-500">Accommodation</span>
                  <span>{money(bookingDetail.accommodation)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-500">Cleaning fee</span>
                  <span>{money(bookingDetail.cleaning_fee)}</span>
                </div>
                <div className="flex items-center justify-between text-amber-700">
                  <span>Commission ({bookingDetail.commission_rate}%)</span>
                  <span>−{money(bookingDetail.commission)}</span>
                </div>
                {bookingDetail.refunded_amount > 0 && (
                  <div className="flex items-center justify-between text-red-600">
                    <span>Refunded</span>
                    <span>−{money(bookingDetail.refunded_amount)}</span>
                  </div>
                )}
                <div className="flex items-center justify-between pt-2 border-t border-slate-100 text-base">
                  <span className="font-serif">Your net</span>
                  <span className="font-serif text-lg">{money(bookingDetail.net)}</span>
                </div>
              </div>
              {bookingDetail.notes && (
                <div>
                  <div className="ns-label mb-1">Notes</div>
                  <div className="rounded-lg bg-stone-50 p-3 text-slate-700 whitespace-pre-wrap">
                    {bookingDetail.notes}
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setBookingDetail(null)} className="rounded-full ns-btn-primary px-6">
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editingExpiry} onOpenChange={(v) => !v && setEditingExpiry(null)}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Set expiry date</DialogTitle>
          </DialogHeader>
          <div className="text-sm text-slate-500 mb-4">
            We&apos;ll email you 30 days before your certificate expires, and again on the day
            itself. Leave blank to remove the reminder.
          </div>
          <Input
            data-testid="landlord-doc-expiry-edit"
            type="date"
            value={editingExpiry?.expires_on || ""}
            onChange={(e) => setEditingExpiry((s) => ({ ...s, expires_on: e.target.value }))}
            className="h-11 bg-white"
          />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditingExpiry(null)}>Cancel</Button>
            <Button onClick={saveExpiry} className="rounded-full ns-btn-primary px-6">
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!tenantModal} onOpenChange={(v) => !v && setTenantModal(null)}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">
              {tenantModal?.mode === "edit" ? "Edit tenant" : "Add tenant"}
            </DialogTitle>
          </DialogHeader>
          {tenantModal && (
            <div className="space-y-4 text-sm">
              <div>
                <Label className="ns-label">Tenant name</Label>
                <Input
                  data-testid="tenant-name"
                  className="mt-2 h-11 bg-white"
                  value={tenantModal.tenant.tenant_name}
                  onChange={(e) => setTenantModal((m) => ({ ...m, tenant: { ...m.tenant, tenant_name: e.target.value } }))}
                />
              </div>
              <div>
                <Label className="ns-label">Tenant email (optional)</Label>
                <Input
                  type="email"
                  className="mt-2 h-11 bg-white"
                  value={tenantModal.tenant.tenant_email}
                  onChange={(e) => setTenantModal((m) => ({ ...m, tenant: { ...m.tenant, tenant_email: e.target.value } }))}
                />
              </div>
              <div>
                <Label className="ns-label">Property</Label>
                <select
                  className="mt-2 h-11 w-full rounded-md border border-slate-200 bg-white px-3"
                  value={tenantModal.tenant.property_id || ""}
                  onChange={(e) => setTenantModal((m) => ({ ...m, tenant: { ...m.tenant, property_id: e.target.value } }))}
                >
                  <option value="">— No property linked —</option>
                  {(landlord?.properties || []).map((p) => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label className="ns-label">Rental address (used on the statement)</Label>
                <Input
                  className="mt-2 h-11 bg-white"
                  value={tenantModal.tenant.address}
                  onChange={(e) => setTenantModal((m) => ({ ...m, tenant: { ...m.tenant, address: e.target.value } }))}
                />
              </div>
              <div>
                <Label className="ns-label">Monthly rent £</Label>
                <Input
                  type="number"
                  step="0.01"
                  min={0}
                  className="mt-2 h-11 bg-white"
                  value={tenantModal.tenant.monthly_rent}
                  onChange={(e) => setTenantModal((m) => ({ ...m, tenant: { ...m.tenant, monthly_rent: e.target.value } }))}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setTenantModal(null)}>Cancel</Button>
            <Button data-testid="tenant-save" onClick={saveTenant} className="rounded-full ns-btn-primary px-6">
              {tenantModal?.mode === "edit" ? "Save changes" : "Add tenant"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
